import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { useSettings } from "../context/SettingsContext";

// ---- Editable stops config (add an 8th object -> renders an 8th stop automatically) ----
const STOPS = [
  { title: "Where Do I Start?", help: "Turn AI into your daily thinking partner with a simple 7-day starter challenge.", platform: "TikTok", duration: "1:00", url: "#" },
  { title: "Which Tool Do I Use?", help: "Stop app-hopping. Pick one weekly annoyance and let AI solve just that.", platform: "TikTok", duration: "1:00", url: "#" },
  { title: "Why Are My Answers Bad?", help: "Get better results instantly with the five words that change everything.", platform: "TikTok", duration: "1:15", url: "#" },
  { title: "Do I Need to Code?", help: "Build something genuinely useful today — with zero code.", platform: "TikTok", duration: "1:00", url: "#" },
  { title: "Will AI Take My Job?", help: "Future-proof your career with one simple weekly habit.", platform: "TikTok", duration: "1:15", url: "#" },
  { title: "Which AI Is Best?", help: "Find your favorite AI with a quick, honest three-model test.", platform: "TikTok", duration: "1:00", url: "#" },
  { title: "Can I Build My Own AI?", help: "Create a personal AI assistant that actually remembers you.", platform: "TikTok", duration: "1:15", url: "#" },
];

// Desktop stage geometry
const STAGE_W = 980;
const CARD_W = 300;
const NODE_XL = 380;
const NODE_XR = 600;
const NODE_Y0 = 80;
const SPACING = 235;
const NODE_R = 26;

const PlatformPill = ({ platform }) => (
  <span
    className={`absolute left-3 top-3 rounded-full px-2.5 py-0.5 text-[11px] font-bold tracking-wide ${
      platform === "YouTube" ? "bg-brick text-white" : "bg-bone text-navy"
    }`}
  >
    {platform}
  </span>
);

function StopCard({ stop, n }) {
  return (
    <a
      href={stop.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Stop ${n}: ${stop.title} — watch on ${stop.platform}`}
      data-testid={`beginner-stop-${n}`}
      className="group block w-full overflow-hidden rounded-2xl border border-[#E6DCC8] bg-white shadow-card transition-transform duration-200 hover:-translate-y-[3px] hover:shadow-hero focus:outline-none focus-visible:ring-2 focus-visible:ring-ochre focus-visible:ring-offset-2"
    >
      {/* Thumbnail strip */}
      <div className="relative h-24" style={{ background: "linear-gradient(135deg,#1E3556 0%,#28456E 100%)" }}>
        <PlatformPill platform={stop.platform} />
        <span className="absolute left-1/2 top-1/2 grid h-12 w-12 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-ochre text-navy shadow-md transition-transform duration-200 group-hover:scale-110">
          <Play size={20} fill="currentColor" className="ml-0.5" aria-hidden="true" />
        </span>
        <span className="absolute bottom-2 right-2 rounded-md bg-navy/90 px-2 py-0.5 text-[11px] font-semibold text-bone">
          {stop.duration}
        </span>
      </div>
      {/* Body */}
      <div className="p-5">
        <p className="eyebrow">Stop {n}</p>
        <h3 className="mt-2 font-serif text-2xl leading-tight text-navy">{stop.title}</h3>
        <p className="mt-2 text-[15px] leading-relaxed text-text-muted">{stop.help}</p>
        <span className="mt-3 inline-block font-sans text-[13px] font-bold uppercase tracking-[0.12em] text-ochre transition-colors group-hover:text-ochre-hover">
          Watch →
        </span>
      </div>
    </a>
  );
}

export default function ForBeginners() {
  const { settings } = useSettings();
  useSeo({
    title: "For Beginners — Practical AI for Everyday Life",
    description:
      'New to AI? Follow a simple 7-video path from "what even is AI?" to building your own AI assistant. No experience needed.',
  });

  // Derive node points + weaving path geometry from the stops array
  const geo = useMemo(() => {
    const n = STOPS.length;
    const nodes = STOPS.map((_, i) => ({
      n: i + 1,
      x: (i + 1) % 2 === 1 ? NODE_XL : NODE_XR,
      y: NODE_Y0 + i * SPACING,
    }));
    const stageH = NODE_Y0 + (n - 1) * SPACING + 200;
    const pts = [[STAGE_W / 2, 0], ...nodes.map((nd) => [nd.x, nd.y]), [STAGE_W / 2, stageH]];
    let d = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 1; i < pts.length; i++) {
      const [x0, y0] = pts[i - 1];
      const [x1, y1] = pts[i];
      const cy = (y0 + y1) / 2;
      d += ` C ${x0} ${cy}, ${x1} ${cy}, ${x1} ${y1}`;
    }
    return { nodes, stageH, d };
  }, []);

  return (
    <div>
      {/* ============ HERO ============ */}
      <section className="on-navy relative overflow-hidden bg-navy grain" data-testid="beginners-hero">
        <div className="container-brand relative flex flex-col items-center py-20 text-center md:py-28">
          <p className="eyebrow !text-ochre-bright">For Beginners</p>
          <h1 className="mt-4 max-w-3xl font-serif text-5xl leading-[1.02] text-bone md:text-6xl lg:text-7xl">
            Never touched AI?
            <br />
            <span className="italic text-ochre-bright">Start right here.</span>
          </h1>
          <p className="mt-6 max-w-measure text-lg text-on-navy-body">
            A simple, no-jargon path from "what even <span className="italic">is</span> AI?" to building your own AI
            assistant — one short video at a time. You'll be treated like a 5th grader the whole way. That's a
            compliment.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {["7 short videos", "~8 minutes total", "No experience needed"].map((chip) => (
              <span
                key={chip}
                className="rounded-full border border-ochre-bright/60 px-4 py-2 text-sm font-medium text-on-navy-body"
              >
                {chip}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PATH ============ */}
      <section className="bg-warm grain" data-testid="beginners-path">
        <div className="container-brand py-20 md:py-24">
          {/* Head */}
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-serif text-4xl text-navy md:text-5xl">
              Your beginner <span className="italic text-ochre">path</span>
            </h2>
            <p className="mt-4 text-[17px] text-text-muted">
              Follow the trail top to bottom. Each stop is one short video that answers one question every beginner has.
            </p>
          </div>

          {/* START cap */}
          <div className="mx-auto mt-14 max-w-xl text-center">
            <p className="eyebrow">Start Here</p>
            <h4 className="mt-2 font-serif text-3xl text-navy">"I have no clue where to begin."</h4>
            <p className="mt-3 text-[15px] text-text-muted">
              Perfect. That's exactly who this is for. New?{" "}
              <Link to="/nerd-scale" className="prose-link" data-testid="beginners-nerd-scale-link">
                Watch the Nerd Scale
              </Link>{" "}
              first.
            </p>
          </div>

          {/* ---- DESKTOP winding trail (>=1000px) ---- */}
          <div className="mt-8 hidden lg:block">
            <div className="relative mx-auto" style={{ width: STAGE_W, height: geo.stageH }}>
              <svg
                className="absolute inset-0"
                width={STAGE_W}
                height={geo.stageH}
                viewBox={`0 0 ${STAGE_W} ${geo.stageH}`}
                fill="none"
                aria-hidden="true"
              >
                <path d={geo.d} stroke="#C08A2E" strokeWidth="3" strokeDasharray="2 12" strokeLinecap="round" opacity="0.85" />
                {/* short connectors node -> card */}
                {geo.nodes.map((nd) => {
                  const isLeft = nd.n % 2 === 1;
                  const cardEdge = isLeft ? NODE_XL - CARD_W - 44 + CARD_W : NODE_XR + 44;
                  return (
                    <line
                      key={`c-${nd.n}`}
                      x1={nd.x}
                      y1={nd.y}
                      x2={cardEdge}
                      y2={nd.y}
                      stroke="#C08A2E"
                      strokeWidth="2.5"
                      strokeDasharray="2 8"
                      strokeLinecap="round"
                      opacity="0.7"
                    />
                  );
                })}
                {/* nodes */}
                {geo.nodes.map((nd) => (
                  <g key={`n-${nd.n}`}>
                    <circle cx={nd.x} cy={nd.y} r={NODE_R} fill="#16283C" stroke="#C08A2E" strokeWidth="3" />
                    <text
                      x={nd.x}
                      y={nd.y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontFamily="Newsreader, serif"
                      fontSize="22"
                      fill="#E0A94A"
                    >
                      {nd.n}
                    </text>
                  </g>
                ))}
              </svg>

              {geo.nodes.map((nd, i) => {
                const isLeft = nd.n % 2 === 1;
                const left = isLeft ? NODE_XL - CARD_W - 44 : NODE_XR + 44;
                return (
                  <div key={`card-${nd.n}`} className="absolute" style={{ left, top: nd.y - 52, width: CARD_W }}>
                    <StopCard stop={STOPS[i]} n={nd.n} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* ---- MOBILE / TABLET stacked column (<1000px) ---- */}
          <div className="relative mx-auto mt-12 max-w-md lg:hidden">
            <div className="absolute bottom-0 left-[19px] top-0 border-l-2 border-dashed border-ochre/50" aria-hidden="true" />
            <div className="space-y-7">
              {STOPS.map((stop, i) => (
                <div key={`m-${i}`} className="relative pl-14">
                  <div className="absolute left-0 top-6 grid h-10 w-10 place-items-center rounded-full bg-navy font-serif text-lg text-ochre-bright ring-2 ring-ochre">
                    {i + 1}
                  </div>
                  <StopCard stop={stop} n={i + 1} />
                </div>
              ))}
            </div>
          </div>

          {/* FINISH cap */}
          <div className="mx-auto mt-16 max-w-xl text-center">
            <p className="eyebrow">You Made It</p>
            <h4 className="mt-2 font-serif text-3xl text-navy">You're not a beginner anymore.</h4>
            <p className="mt-3 text-[15px] text-text-muted">
              Seven videos ago you had no clue. Now go build something.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
              <a
                href={settings.youtube_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                data-testid="beginners-youtube-cta"
              >
                Subscribe on YouTube
              </a>
              <Link to="/setups" className="btn btn-secondary" data-testid="beginners-setups-cta">
                Browse the free setups
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
